import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface LoadingScreenProps {
  isLoading: boolean;
}

export function LoadingScreen({ isLoading }: LoadingScreenProps) {
  const [shouldRender, setShouldRender] = useState(isLoading);
  const [isVisible, setIsVisible] = useState(isLoading);

  useEffect(() => {
    if (isLoading) {
      setShouldRender(true);
      setIsVisible(true);
    } else {
      // Start fade out
      setIsVisible(false);
      // Remove from DOM after animation
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match transition duration
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  if (!shouldRender) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] flex items-center justify-center',
        'bg-gradient-to-br from-slate-900/95 via-slate-900/98 to-slate-950/95',
        'backdrop-blur-sm transition-opacity duration-300',
        isVisible ? 'opacity-100' : 'opacity-0'
      )}
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-blue-500/10 via-transparent to-transparent rounded-full animate-pulse" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-purple-500/10 via-transparent to-transparent rounded-full animate-pulse delay-1000" />
      </div>

      {/* Loading card */}
      <div
        className={cn(
          'relative flex flex-col items-center gap-8 p-12',
          'bg-gradient-to-br from-white/10 to-white/5',
          'dark:from-white/10 dark:to-white/5',
          'backdrop-blur-xl rounded-3xl',
          'border border-white/20',
          'shadow-2xl shadow-black/20',
          'transform transition-all duration-300',
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        )}
      >
        {/* Logo with glow effect */}
        <div className="relative">
          {/* Glow behind logo */}
          <div className="absolute inset-0 blur-2xl bg-blue-500/30 animate-pulse rounded-full scale-150" />

          {/* Logo */}
          <img
            src="/pos-logo.png"
            alt="POS Logo"
            className="relative h-24 w-auto animate-pulse drop-shadow-2xl"
          />
        </div>

        {/* Loading text and spinner */}
        <div className="flex flex-col items-center gap-4">
          <h2 className="text-xl font-semibold text-white/90 tracking-wide">
            Thermalink Project Management
          </h2>

          {/* Spinner and text */}
          <div className="flex items-center gap-3">
            {/* Custom spinner */}
            <div className="relative w-5 h-5">
              <div className="absolute inset-0 rounded-full border-2 border-white/20" />
              <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-blue-400 animate-spin" />
            </div>

            <span className="text-white/70 text-sm font-medium">
              Loading your data
              <LoadingDots />
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-64 h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-loading-bar"
          />
        </div>
      </div>

      {/* Inline styles for animations */}
      <style>{`
        @keyframes loading-bar {
          0% {
            width: 0%;
            margin-left: 0%;
          }
          50% {
            width: 60%;
            margin-left: 20%;
          }
          100% {
            width: 0%;
            margin-left: 100%;
          }
        }
        .animate-loading-bar {
          animation: loading-bar 1.5s ease-in-out infinite;
        }
        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
}

// Animated dots component
function LoadingDots() {
  return (
    <span className="inline-flex w-6">
      <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
      <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
      <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
    </span>
  );
}
